import { useLayoutEffect, useRef, useState, type Dispatch } from "react";
import { useSearchParams, useRouter } from "@/routing/navigation";
import { toast } from "sonner";
import {
  resolvePlannedSampleSize,
  studySpecOf,
  type AudienceFilterLabel,
  type SelectedSegment,
} from "@digital-twins/shared-types";
import i18n from "@/i18n";
import { getQuestionnaireDraftApi } from "@/features/studies/questionnaire/api/questionnaireDraftsApi";
import { readUntilAnswered } from "@/features/studies/questionnaire/readUntilAnswered";
import { pageReadFinal, readRefused } from "@/lib/api/transportFailure";
import type { PopulationFilter } from "@/types/routes/test-results";
import { STUDY_CREATION_TYPE } from "../types";
import { getGoalById } from "../components/goals/goalsConfig";
import type { CreationState, CreationAction } from "../state/creationState";
import { usableStudyName } from "../utils/studyName";

/**
 * Fresh-create vs. explicit draft resume for the study creation wizard.
 *
 * - Plain `/tests/new`: creation always starts fresh. The old "resume
 *   unfinished study" prompt is gone;
 *   drafts are persisted server-side and resumed from the Studies lobby.
 *   Browser back from `?draftId=` is the same route — this hook then
 *   `RESET_TO_FRESH` so the goal screen does not stack on the builder.
 * - `/tests/new?draftId=<id>`: fetches that specific draft, preserves its id
 *   in wizard state, restores available audience / planned sample size / filter state
 *   and Review's launch options,
 *   and jumps to the build step. The draft owns the filter and the saved
 *   segments (C09-2); Review sizes the draft's filter itself, as it does live
 *   (`useReviewedFilteredCount`). Falls back to the audience step when the
 *   draft has no audience metadata.
 * - The flow writes the draft it holds into the URL once the builder creates
 *   it (`StudyCreationFlow`), so a reload reopens it. That id is the flow's
 *   own draft, not a request to resume one: nothing is fetched or restored.
 * - Any other draft the URL names (Back to an earlier draft, the "Continue
 *   draft" banner) is a real resume, and it replaces the draft the flow
 *   holds. `isResumingDraft` is true from its start to its end, so the flow
 *   shows its loader in place of the builder: the builder loads its draft
 *   once, on mount, and mounts again on the resumed one. The builder's draft
 *   and the flow's (the one Launch sends) cannot differ. The held draft's
 *   wizard state goes first (`RESET_TO_FRESH`), as on a fresh page load.
 *
 * A layout effect, so a changed URL never paints a frame of the step it
 * leaves (the audience picker flashed on "Continue draft").
 */
export function useStudyCreationDraftResume(
  state: CreationState,
  dispatch: Dispatch<CreationAction>,
): { isResumingDraft: boolean } {
  const searchParams = useSearchParams();
  const router = useRouter();
  const draftId = searchParams.get("draftId");
  const [isResumingDraft, setIsResumingDraft] = useState(Boolean(draftId));
  const previousDraftIdRef = useRef<string | null>(draftId);
  // Read when the URL's draft changes, never as a reason to re-run.
  const heldDraftIdRef = useRef(state.questionnaireDraftId);
  heldDraftIdRef.current = state.questionnaireDraftId;

  useLayoutEffect(() => {
    if (!draftId) {
      // Browser back from `/tests/new?draftId=` stays on this route. The
      // resumed wizard must be cleared before the fresh goal screen returns.
      if (previousDraftIdRef.current) {
        dispatch({ type: "RESET_TO_FRESH" });
      }
      previousDraftIdRef.current = null;
      setIsResumingDraft(false);
      return;
    }

    previousDraftIdRef.current = draftId;
    if (draftId === heldDraftIdRef.current) {
      return;
    }

    setIsResumingDraft(true);
    if (heldDraftIdRef.current) {
      dispatch({ type: "RESET_TO_FRESH" });
    }

    // `cancelled` drops a resume the URL has moved on from (a Strict Mode
    // re-run, or another draft named before this one loaded): only the
    // current one dispatches and ends the loader.
    // The draft read is sent again while it gets no answer (offline, the
    // backend restarting, a 5xx), as the builder's reads are: a blip on a
    // reload never drops the user back to the audience step (MT-06). Any
    // other failure does.
    const superseded = new AbortController();
    let cancelled = false;

    const resume = async () => {
      try {
        const restored = await readUntilAnswered(
          () => getQuestionnaireDraftApi(draftId),
          superseded.signal,
          pageReadFinal,
        );
        if (cancelled) return;
        // A copy the server refused holds only its placeholder. As on its
        // lobby card, it does not open: say so instead of an empty study.
        if (restored.status === "duplicate_failed") {
          toast.error(i18n.t("studyLobby.duplicateFailed", { ns: "app" }));
          dispatch({ type: "SET_STEP", payload: 1 });
          return;
        }

        // Restore the server's saved route, not a competing query parameter or
        // a guess from asset/template labels. Clear a previous draft's goal when
        // this draft predates goals or carries an unknown one.
        const restoredGoal = getGoalById(restored.goalId ?? null);
        dispatch(
          restoredGoal
            ? { type: "SET_GOAL", payload: restoredGoal.id }
            : { type: "CLEAR_GOAL" },
        );
        dispatch({ type: "SELECT_STUDY_TYPE", payload: STUDY_CREATION_TYPE });
        dispatch({ type: "SET_QUESTIONNAIRE_DRAFT_ID", payload: draftId });

        // The display name identifies the study to people; the questionnaire
        // title may be planner-authored and is used by twins.
        const restoredTitle =
          usableStudyName(restored.displayName) || usableStudyName(restored.title);
        if (restoredTitle) {
          dispatch({ type: "SET_STUDY_NAME", payload: restoredTitle });
        }

        // Review's launch options as Review stored them (`changeReviewOptions`).
        // Before the sample size below, which a quick study's cap would clamp.
        const reviewOptions = restored.artifacts?.draft?.reviewOptions;
        if (reviewOptions) {
          dispatch({ type: "SET_STUDY_MODE", payload: reviewOptions.studyMode });
          dispatch({ type: "SET_DECK_ENABLED", payload: reviewOptions.deckEnabled });
          dispatch({
            type: "SET_TWIN_CHAT_ENABLED",
            payload: reviewOptions.twinChatEnabled,
          });
        }

        // The stored survey, so the design resolves before the sample size is
        // restored below. It pins nothing: Review opens only through
        // `enterReview`, which compiles and checks the draft.
        const compiledSurveyJson = restored.artifacts?.compiledSurveyJson;
        if (compiledSurveyJson && typeof compiledSurveyJson === "object") {
          dispatch({
            type: "RESTORE_SURVEY",
            payload: compiledSurveyJson as Record<string, unknown>,
          });
        }

        const PENDING_POPULATION_ID = "population_pending_selection";
        const audience = restored.artifacts?.draft?.audience;
        const populationId = audience?.populationId;
        const hasRealAudience =
          Boolean(populationId) && populationId !== PENDING_POPULATION_ID;
        // `estimatedRespondentCount` is the audience pool (how many twins
        // exist). Sample size is stored separately. A usable stored integer
        // is applied after design resolution so a size below the design
        // target is not raised. With nothing stored, start from the product
        // default first and let that resolution raise it the same way a
        // fresh session does. Copying the pool here is what launched studies
        // at the full audience (RND-2794) and what review showed after a
        // reload (RND-2799).
        const storedSampleSize = audience?.plannedSampleSize;
        const plannedSampleSize = resolvePlannedSampleSize(storedSampleSize);
        const hasStoredSampleSize = storedSampleSize === plannedSampleSize;

        if (hasRealAudience) {
          dispatch({ type: "SELECT_POPULATION", payload: populationId! });

          const filter = (audience?.populationFilter ?? null) as
            | PopulationFilter
            | null;
          const selectedSegments = (audience?.selectedSegments ??
            []) as Array<SelectedSegment>;
          if (filter || selectedSegments.length > 0) {
            // The pick the builder writes back. Review sizes the draft's own
            // filter (`useReviewedFilteredCount`). The filter's labels come
            // back with it (C09-4), so the next audience write keeps them.
            const filterLabels = (audience?.populationFilterLabels ??
              []) as Array<AudienceFilterLabel>;
            dispatch({
              type: "SET_POPULATION_FILTER",
              payload: {
                filter,
                ...(selectedSegments.length > 0 ? { selectedSegments } : {}),
                ...(filterLabels.length > 0 ? { filterLabels } : {}),
              },
            });
          }

          if (!hasStoredSampleSize) {
            dispatch({
              type: "SET_NUM_RESPONDENTS",
              payload: plannedSampleSize,
            });
          }

          dispatch({ type: "SET_STEP", payload: 2 });
        } else {
          // Audience metadata missing or pending — start at the audience step.
          dispatch({ type: "SET_STEP", payload: 1 });
        }

        // The draft's language is not restored here: the builder derives
        // creation state from the committed draft when it mounts, after this
        // audience select (C12-2), so it wins over the preselect (RND-2668).

        // The design the draft's StudySpec resolved (plan S3.4 row 38).
        dispatch({
          type: "SET_SPEC_DESIGN",
          payload:
            studySpecOf(restored.artifacts?.draft)?.resolved.design ?? null,
        });

        // After design resolution, so a size the marketer saved is the one
        // review shows, including when it is below the design's target.
        if (hasRealAudience && hasStoredSampleSize) {
          dispatch({
            type: "SET_NUM_RESPONDENTS",
            payload: plannedSampleSize,
          });
        }
      } catch (error) {
        console.error("Failed to resume questionnaire draft:", error);
        if (!cancelled) {
          if (readRefused(error)) {
            toast.error(i18n.t("creation.draftNotFound", { ns: "app" }));
            router.replace("/tests/new");
            dispatch({ type: "RESET_TO_FRESH" });
          } else {
            toast.error(i18n.t("creation.failedResumeDraft", { ns: "app" }));
            dispatch({ type: "SET_STEP", payload: 1 });
          }
        }
      } finally {
        if (!cancelled) setIsResumingDraft(false);
      }
    };

    void resume();

    return () => {
      cancelled = true;
      superseded.abort();
    };
  }, [draftId, dispatch, router]);

  return { isResumingDraft };
}
